import { z } from 'zod';

import type { AsContract, MatchesSchema } from '@/shared/api/contract';

/**
 * Kampania (spec 7A, decyzje 84 i 86) — <b>to samo zobowiązanie nałożone na zbiór elementów</b>,
 * opisany selektorem, a nie wyliczony ręcznie.
 *
 * ⚠ Zbiór jest **dynamiczny**: element, który powstanie jutro i pasuje do selektora, dostanie
 * zadanie sam. Robi to **uzgadnianie** (`reconcile`), a nie powiadamianie — dlatego ekran ma po
 * co wołać uzgodnienie przy wejściu, zamiast czekać na zdarzenie, którego nikt nie wysyła.
 */

/** ⚠ `launched` znaczy **„aktywna"**, a nie „już się wydarzyła" — patrz decyzja 84. */
export const campaignStatusSchema = z.enum(['draft', 'launched', 'closed']);

export type CampaignStatus = z.infer<typeof campaignStatusSchema>;

export const selectorOwnerKindSchema = z.enum(['user', 'team']);

export type SelectorOwnerKind = z.infer<typeof selectorOwnerKindSchema>;

/** Warunki zbioru. Puste pole znaczy „bez tego warunku". */
// ⚠ Pola są **opcjonalne, nie tylko puste** — generator dokumentu wywodzi to z wartości
// domyślnych w konstruktorze rekordu. Schemat musi odbijać kontrakt, a nie to, co serwer
// akurat zawsze wysyła: asercja niżej porównuje typy w OBIE strony.
export const campaignSelectorSchema = z.object({
  type: z.string().nullable().optional(),
  ownerKind: selectorOwnerKindSchema.nullable().optional(),
  ownerId: z.number().nullable().optional(),
  titleContains: z.string().nullable().optional(),
});

export type CampaignSelector = AsContract<z.infer<typeof campaignSelectorSchema>>;

/**
 * Tryb aktywacji szablonu (decyzja 88) — **wybór jednokrotny**:
 * `now` (domyślne) — zadania rodzą się aktywne · `iterationNext` — następna iteracja każdego
 * elementu z osobna · `surt` — uśpione do domknięcia wskazanego zobowiązania.
 */
export const campaignActivationModeSchema = z.enum(['now', 'iterationNext', 'surt']);

export type CampaignActivationMode = z.infer<typeof campaignActivationModeSchema>;

/** Szablon zadania nakładanego na każdy element ze zbioru. */
export const campaignTemplateSchema = z.object({
  title: z.string(),
  body: z.string().nullable().optional(),
  activation: campaignActivationModeSchema.optional(),
  /** Jedno konkretne zobowiązanie (decyzja 90) — wyłącznie przy trybie `surt`. */
  surtSourceCommitmentId: z.number().nullable().optional(),
});

export type CampaignTemplate = AsContract<z.infer<typeof campaignTemplateSchema>>;

export const campaignSummarySchema = z.object({
  id: z.number(),
  title: z.string(),
  description: z.string().nullable(),
  selector: campaignSelectorSchema,
  template: campaignTemplateSchema,
  deadlineUtc: z.string().nullable(),
  status: campaignStatusSchema,
  createdAtUtc: z.string(),
  launchedAtUtc: z.string().nullable(),
  closedAtUtc: z.string().nullable(),
});

export type CampaignSummary = AsContract<z.infer<typeof campaignSummarySchema>>;

/** Zalegający — kto i na czym. */
export const campaignLaggardSchema = z.object({
  commitmentId: z.number(),
  elementId: z.number(),
  elementTitle: z.string(),
  ownerNames: z.array(z.string()),
});

export type CampaignLaggard = z.infer<typeof campaignLaggardSchema>;

/**
 * Postęp kampanii.
 *
 * ⭐ `obsolete` jest **osobno od `done`** i to nie jest formalność: zlanie ich w jedno „zamknięte"
 * ukryłoby dokładnie tę informację, po którą jest druga oś statusu — ilu ludzi uznało, że kampania
 * ich nie dotyczy.
 */
export const campaignRollupSchema = z.object({
  total: z.number(),
  done: z.number(),
  obsolete: z.number(),
  open: z.number(),
  overdue: z.number(),
  laggards: z.array(campaignLaggardSchema),
});

export type CampaignRollup = z.infer<typeof campaignRollupSchema>;

/**
 * Źródło surta kampanii — na co czekają zadania i czy to jeszcze możliwe.
 *
 * ⚠ `obsolete` znaczy: źródło zamknięto jako **nieaktualne**, więc uzgadnianie pomija kampanię
 * z powodem (decyzja 89). Ekran ma o tym mówić wprost — i **nie wołać uzgodnienia**, bo wynikiem
 * byłby ten sam komunikat przy każdym wejściu.
 */
export const campaignSurtSourceSchema = z.object({
  commitmentId: z.number(),
  title: z.string(),
  hostElementId: z.number(),
  hostElementTitle: z.string(),
  done: z.boolean(),
  obsolete: z.boolean(),
});

export type CampaignSurtSource = z.infer<typeof campaignSurtSourceSchema>;

export const campaignDetailSchema = z.object({
  campaign: campaignSummarySchema,
  /** Puste dla szkicu — nie ma jeszcze czego liczyć. */
  rollup: campaignRollupSchema.nullable(),
  /** Tylko przy trybie `surt`. */
  surtSource: campaignSurtSourceSchema.nullable().optional(),
});

export type CampaignDetail = AsContract<z.infer<typeof campaignDetailSchema>>;

export const campaignTargetSchema = z.object({
  elementId: z.number(),
  title: z.string(),
  ownerNames: z.array(z.string()),
});

export type CampaignTarget = z.infer<typeof campaignTargetSchema>;

/** Zapowiedź uruchomienia — <b>bez zapisu</b>. Decyzja o wsypaniu roboty setkom ludzi nie zapada w ciemno. */
export const campaignPreviewSchema = z.object({
  matchedCount: z.number(),
  sample: z.array(campaignTargetSchema),
  selectorIsEmpty: z.boolean(),
});

export type CampaignPreview = z.infer<typeof campaignPreviewSchema>;

/**
 * Wynik uzgodnienia.
 *
 * ⭐ `dropped` to elementy, które **wypadły ze zbioru**, ale zadanie zachowały — nic nie usuwamy
 * (sek. 11). Człowiek zamyka takie zadanie jako **nieaktualne**; ekran pokazuje je osobno.
 */
export const campaignReconcileResultSchema = z.object({
  created: z.number(),
  matched: z.number(),
  dropped: z.number(),
});

export type CampaignReconcileResult = z.infer<typeof campaignReconcileResultSchema>;

// ── Wejście ─────────────────────────────────────────────────────────────────

export interface CreateCampaignInput {
  title: string;
  template: {
    title: string;
    body?: string | null;
    activation?: CampaignActivationMode;
    surtSourceCommitmentId?: number | null;
  };
  selector: {
    type?: string | null;
    ownerKind?: SelectorOwnerKind | null;
    ownerId?: number | null;
    titleContains?: string | null;
  };
  description?: string | null;
  deadlineUtc?: string | null;
}

// ── Spinacz z kontraktem API ────────────────────────────────────────────────
//
// Każdy schemat `zod` zestawiony ze schematem z dokumentu OpenAPI (decyzja 74). Bez tych par
// schematy modułu nie są z niczym związane, a rozjazd wraca do wykrywania w przeglądarce.

export const campaignsContract = {
  CampaignSummary: true satisfies MatchesSchema<CampaignSummary, 'CampaignSummary'>,
  CampaignStatus: true satisfies MatchesSchema<CampaignStatus, 'CampaignStatusDto'>,
  CampaignSelector: true satisfies MatchesSchema<CampaignSelector, 'CampaignSelector'>,
  CampaignTemplate: true satisfies MatchesSchema<CampaignTemplate, 'CampaignTemplate'>,
  CampaignActivationMode: true satisfies MatchesSchema<
    CampaignActivationMode,
    'CampaignActivationMode'
  >,
  CampaignSurtSource: true satisfies MatchesSchema<CampaignSurtSource, 'CampaignSurtSource'>,
  CampaignDetail: true satisfies MatchesSchema<CampaignDetail, 'CampaignDetail'>,
  CampaignRollup: true satisfies MatchesSchema<CampaignRollup, 'CampaignRollup'>,
  CampaignLaggard: true satisfies MatchesSchema<CampaignLaggard, 'CampaignLaggard'>,
  CampaignPreview: true satisfies MatchesSchema<CampaignPreview, 'CampaignPreview'>,
  CampaignTarget: true satisfies MatchesSchema<CampaignTarget, 'CampaignTarget'>,
  CampaignReconcileResult: true satisfies MatchesSchema<
    CampaignReconcileResult,
    'CampaignReconcileResult'
  >,
  // ⚠ W dokumencie ten typ jest **nullowalny** (selektor bez filtra właściciela), więc para
  // musi to powiedzieć wprost — inaczej asercja zgłasza rozjazd tam, gdzie go nie ma.
  SelectorOwnerKind: true satisfies MatchesSchema<SelectorOwnerKind | null, 'SelectorOwnerKind'>,
} as const;
